import React from 'react';
import {
  Dialog,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Alert,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Card,
  CardContent,
  Fade,
  Slide,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { countryCodes, THEME_COLORS, TYPOGRAPHY, ANIMATIONS } from '../constants';
import { activityRegistrationService } from '../apiService';
import {
  validateAccompanistData,
  validateSoldierEmailData,
  validateSoldierAdditionalData,
  isAccompanistDataValid,
  isSoldierEmailDataValid,
  isSoldierAdditionalDataValid
} from '../utils';

/**
 * Composant modal pour ajouter un participant
 */
const ParticipantModal = ({
  modalOpen,
  onClose,
  participantType,
  modalStep,
  leadFound,
  searchingLead,
  formErrors,
  accompanistData,
  soldierData,
  onParticipantTypeChange,
  onAccompanistDataChange,
  onSoldierDataChange,
  onAddParticipant,
  setErrors,
  setLeadSearching,
  setFoundLead,
  goToStep
}) => {

  /**
   * Gestion du passage à l'étape suivante
   */
  const handleNext = async () => {
    if (participantType === 'accompagnateur') {
      // Validation et ajout de l'accompagnateur
      const errors = validateAccompanistData(accompanistData);
      setErrors(errors);
      
      if (Object.keys(errors).length === 0) {
        onAddParticipant('accompagnateur');
      }
    } else if (participantType === 'futur_soldat') {
      if (modalStep === 1) {
        // Validation des emails
        const errors = validateSoldierEmailData(soldierData);
        setErrors(errors);
        
        if (Object.keys(errors).length === 0) {
          // Recherche du lead par email
          await searchLeadByEmail();
        }
      } else if (modalStep === 2) {
        // Validation des données supplémentaires et ajout
        const errors = validateSoldierAdditionalData(soldierData);
        setErrors(errors);
        
        if (Object.keys(errors).length === 0) {
          onAddParticipant('futur_soldat');
        }
      }
    }
  };

  /**
   * Recherche d'un lead par email
   */
  const searchLeadByEmail = async () => {
    setLeadSearching(true);
    try {
      const lead = await activityRegistrationService.searchLeadByEmail(soldierData.email);
      setFoundLead(lead);
      
      if (lead) {
        // Lead trouvé, on peut ajouter directement le participant
        onAddParticipant('futur_soldat', lead);
      } else {
        // Aucun lead trouvé, passer à l'étape suivante pour saisie manuelle
        goToStep(2);
      }
    } catch (error) {
      console.error('Erreur lors de la recherche du lead:', error);
      setErrors({ email: 'Erreur lors de la recherche. Veuillez réessayer.' });
    } finally {
      setLeadSearching(false);
    }
  };

  /**
   * Obtient les étapes du stepper selon le type de participant
   */
  const getSteps = () => {
    if (participantType === 'accompagnateur') {
      return ['Type', 'Informations'];
    } else if (participantType === 'futur_soldat') {
      if (leadFound) {
        return ['Type', 'Email', 'Confirmation'];
      } else {
        return ['Type', 'Email', 'Informations'];
      }
    }
    return ['Type'];
  };

  /**
   * Vérifie si le bouton suivant doit être activé
   */
  const isNextButtonEnabled = () => {
    if (modalStep === 0) {
      return participantType !== '';
    } else if (participantType === 'accompagnateur' && modalStep === 1) {
      // Vérifier qu'il n'y a pas d'erreurs ET que les données sont valides
      const hasErrors = Object.keys(formErrors).some(key => formErrors[key]);
      return !hasErrors && isAccompanistDataValid(accompanistData);
    } else if (participantType === 'futur_soldat' && modalStep === 1) {
      // Vérifier qu'il n'y a pas d'erreurs ET que les données sont valides
      const hasErrors = Object.keys(formErrors).some(key => formErrors[key]);
      return !hasErrors && isSoldierEmailDataValid(soldierData);
    } else if (participantType === 'futur_soldat' && modalStep === 2) {
      // Vérifier qu'il n'y a pas d'erreurs ET que les données sont valides
      const hasErrors = Object.keys(formErrors).some(key => formErrors[key]);
      return !hasErrors && isSoldierAdditionalDataValid(soldierData);
    }
    return false;
  };

  /**
   * Rendu du contenu selon l'étape actuelle
   */
  const renderStepContent = () => {
    // Style uniforme pour les champs de saisie (accessible dans toutes les étapes)
    const fieldStyle = {
      '& .MuiOutlinedInput-root': {
        borderRadius: 3,
        backgroundColor: THEME_COLORS.background.main,
        fontSize: TYPOGRAPHY.fontSize.base,
        fontFamily: TYPOGRAPHY.fontFamily.primary,
        transition: ANIMATIONS.transition.normal,
        '& fieldset': {
          borderColor: THEME_COLORS.border.main,
          borderWidth: '1.5px'
        },
        '&:hover fieldset': {
          borderColor: participantType === 'accompagnateur' 
            ? THEME_COLORS.primary.light 
            : THEME_COLORS.secondary.light,
          borderWidth: '2px'
        },
        '&.Mui-focused fieldset': {
          borderColor: participantType === 'accompagnateur' 
            ? THEME_COLORS.primary.main 
            : THEME_COLORS.secondary.main,
          borderWidth: '2px',
          boxShadow: `0 0 0 3px ${participantType === 'accompagnateur' 
            ? THEME_COLORS.primary.main 
            : THEME_COLORS.secondary.main}20`
        },
        '&.Mui-error fieldset': {
          borderColor: THEME_COLORS.error.main,
          borderWidth: '2px'
        }
      },
      '& .MuiInputLabel-root': {
        fontFamily: TYPOGRAPHY.fontFamily.primary,
        fontWeight: TYPOGRAPHY.fontWeight.medium,
        fontSize: TYPOGRAPHY.fontSize.base,
        '&.Mui-focused': {
          color: participantType === 'accompagnateur' 
            ? THEME_COLORS.primary.main 
            : THEME_COLORS.secondary.main
        },
        '&.Mui-error': {
          color: THEME_COLORS.error.main
        }
      },
      '& .MuiFormHelperText-root': {
        fontFamily: TYPOGRAPHY.fontFamily.primary,
        fontSize: TYPOGRAPHY.fontSize.sm,
        marginLeft: 2,
        '&.Mui-error': {
          color: THEME_COLORS.error.main
        }
      }
    };

    // Étape 0: Sélection du type de participant
    if (modalStep === 0) {
      return (
        <Box>
          <Typography 
            variant="h6"
            sx={{ 
              textAlign: 'center',
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              fontWeight: TYPOGRAPHY.fontWeight.semibold,
              color: THEME_COLORS.text.primary,
              mb: 2
            }}
          >
            Quel type de participant souhaitez-vous ajouter ?
          </Typography>
          
          <Typography 
            variant="body2"
            sx={{ 
              textAlign: 'center',
              color: THEME_COLORS.text.secondary,
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              mb: 4,
              maxWidth: 400,
              mx: 'auto'
            }}
          >
            Sélectionnez le type qui correspond le mieux à la personne que vous souhaitez inscrire
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {/* Carte Accompagnateur */}
            <Grid item xs={12} md={12} lg={6}>
              <Card
                onClick={() => onParticipantTypeChange('accompagnateur')}
                className="participant-type-card"
                sx={{
                  maxWidth: { xs: 400, lg: 250 },
                  mx: 'auto',
                  p: 3,
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: ANIMATIONS.transition.normal,
                  border: participantType === 'accompagnateur' 
                    ? `2px solid ${THEME_COLORS.primary.main}`
                    : `2px solid ${THEME_COLORS.border.light}`,
                  background: participantType === 'accompagnateur'
                    ? THEME_COLORS.primary.gradient
                    : THEME_COLORS.background.paper,
                  color: participantType === 'accompagnateur' ? 'white' : THEME_COLORS.text.primary,
                  boxShadow: participantType === 'accompagnateur'
                    ? `0 8px 32px ${THEME_COLORS.primary.main}30`
                    : THEME_COLORS.shadow.sm,
                  '&:hover': {
                    transform: { xs: 'translateY(-2px)', lg: 'translateY(-4px)' },
                    boxShadow: participantType === 'accompagnateur'
                      ? `0 12px 40px ${THEME_COLORS.primary.main}40`
                      : THEME_COLORS.shadow.lg
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: { xs: '2.5rem', lg: '3rem' }, mb: 2 }}>
                    👨‍👩‍👧‍👦
                  </Typography>
                  <Typography 
                    variant="h6"
                    sx={{ 
                      fontFamily: TYPOGRAPHY.fontFamily.primary,
                      fontWeight: TYPOGRAPHY.fontWeight.bold,
                      mb: 1,
                      fontSize: { xs: TYPOGRAPHY.fontSize.base, lg: TYPOGRAPHY.fontSize.lg }
                    }}
                  >
                    Accompagnateur
                  </Typography>
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontFamily: TYPOGRAPHY.fontFamily.primary,
                      opacity: 0.9,
                      lineHeight: 1.4,
                      fontSize: { xs: TYPOGRAPHY.fontSize.sm, lg: TYPOGRAPHY.fontSize.base }
                    }}
                  >
                    Famille ou proche accompagnant un futur soldat
                  </Typography>
                  
                  {participantType === 'accompagnateur' && (
                    <Chip 
                      label="✓ Sélectionné"
                      sx={{
                        mt: 2,
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontFamily: TYPOGRAPHY.fontFamily.primary,
                        fontWeight: TYPOGRAPHY.fontWeight.medium,
                        fontSize: { xs: TYPOGRAPHY.fontSize.xs, lg: TYPOGRAPHY.fontSize.sm }
                      }}
                    />
                  )}
                </Box>
              </Card>
            </Grid>

            {/* Carte Futur Soldat */}
            <Grid item xs={12} md={12} lg={6}>
              <Card
                onClick={() => onParticipantTypeChange('futur_soldat')}
                className="participant-type-card"
                sx={{
                  maxWidth: { xs: 400, lg: 250 },
                  mx: 'auto',
                  p: 3,
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: ANIMATIONS.transition.normal,
                  border: participantType === 'futur_soldat' 
                    ? `2px solid ${THEME_COLORS.secondary.main}`
                    : `2px solid ${THEME_COLORS.border.light}`,
                  background: participantType === 'futur_soldat'
                    ? THEME_COLORS.secondary.gradient
                    : THEME_COLORS.background.paper,
                  color: participantType === 'futur_soldat' ? 'white' : THEME_COLORS.text.primary,
                  boxShadow: participantType === 'futur_soldat'
                    ? `0 8px 32px ${THEME_COLORS.secondary.main}30`
                    : THEME_COLORS.shadow.sm,
                  '&:hover': {
                    transform: { xs: 'translateY(-2px)', lg: 'translateY(-4px)' },
                    boxShadow: participantType === 'futur_soldat'
                      ? `0 12px 40px ${THEME_COLORS.secondary.main}40`
                      : THEME_COLORS.shadow.lg
                  }
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: { xs: '2.5rem', lg: '3rem' }, mb: 2 }}>
                    🪖
                  </Typography>
                  <Typography 
                    variant="h6"
                    sx={{ 
                      fontFamily: TYPOGRAPHY.fontFamily.primary,
                      fontWeight: TYPOGRAPHY.fontWeight.bold,
                      mb: 1,
                      fontSize: { xs: TYPOGRAPHY.fontSize.base, lg: TYPOGRAPHY.fontSize.lg }
                    }}
                  >
                    Futur soldat
                  </Typography>
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontFamily: TYPOGRAPHY.fontFamily.primary,
                      opacity: 0.9,
                      lineHeight: 1.4,
                      fontSize: { xs: TYPOGRAPHY.fontSize.sm, lg: TYPOGRAPHY.fontSize.base }
                    }}
                  >
                    Personne qui va effectuer son service militaire
                  </Typography>
                  
                  {participantType === 'futur_soldat' && (
                    <Chip 
                      label="✓ Sélectionné"
                      sx={{
                        mt: 2,
                        backgroundColor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        fontFamily: TYPOGRAPHY.fontFamily.primary,
                        fontWeight: TYPOGRAPHY.fontWeight.medium,
                        fontSize: { xs: TYPOGRAPHY.fontSize.xs, lg: TYPOGRAPHY.fontSize.sm }
                      }}
                    />
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      );
    }

    // Étape 1: Formulaire selon le type
    if (modalStep === 1) {
      if (participantType === 'accompagnateur') {
        return (
          <Box>
            {/* Header de section */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography 
                variant="h6"
                sx={{ 
                  fontFamily: TYPOGRAPHY.fontFamily.primary,
                  fontWeight: TYPOGRAPHY.fontWeight.semibold,
                  color: THEME_COLORS.text.primary,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                👨‍👩‍👧‍👦 Informations de l'accompagnateur
              </Typography>
              <Typography 
                variant="body2"
                sx={{ 
                  color: THEME_COLORS.text.secondary,
                  fontFamily: TYPOGRAPHY.fontFamily.primary
                }}
              >
                Renseignez les coordonnées de la personne accompagnatrice
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Ligne prénom/nom */}
              <Box sx={{ display: 'flex', gap: 3 }}>
                <TextField
                  fullWidth
                  label="Prénom"
                  value={accompanistData.firstName}
                  onChange={onAccompanistDataChange('firstName')}
                  error={!!formErrors.firstName}
                  helperText={formErrors.firstName}
                  required
                  placeholder="Entrez le prénom"
                  sx={fieldStyle}
                />

                <TextField
                  fullWidth
                  label="Nom"
                  value={accompanistData.lastName}
                  onChange={onAccompanistDataChange('lastName')}
                  error={!!formErrors.lastName}
                  helperText={formErrors.lastName}
                  required
                  placeholder="Entrez le nom de famille"
                  sx={fieldStyle}
                />
              </Box>

              {/* Email */}
              <TextField
                fullWidth
                label="Adresse email"
                type="email"
                value={accompanistData.email}
                onChange={onAccompanistDataChange('email')}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
                placeholder="exemple@email.com"
                sx={fieldStyle}
              />

              {/* Numéro de téléphone avec code pays moderne */}
              <Box>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 2, 
                    fontFamily: TYPOGRAPHY.fontFamily.primary,
                    fontWeight: TYPOGRAPHY.fontWeight.medium,
                    color: THEME_COLORS.text.primary
                  }}
                >
                  📞 Numéro de téléphone
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl sx={{ minWidth: 140 }}>
                    <InputLabel
                      sx={{
                        fontFamily: TYPOGRAPHY.fontFamily.primary,
                        '&.Mui-focused': {
                          color: THEME_COLORS.primary.main
                        }
                      }}
                    >
                      Pays
                    </InputLabel>
                    <Select
                      value={accompanistData.countryCode}
                      onChange={onAccompanistDataChange('countryCode')}
                      label="Pays"
                      sx={{
                        borderRadius: 3,
                        backgroundColor: THEME_COLORS.background.main,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: THEME_COLORS.border.main,
                          borderWidth: '1.5px'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: THEME_COLORS.primary.light,
                          borderWidth: '2px'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: THEME_COLORS.primary.main,
                          borderWidth: '2px',
                          boxShadow: `0 0 0 3px ${THEME_COLORS.primary.main}20`
                        }
                      }}
                    >
                      {countryCodes.map((country) => (
                        <MenuItem 
                          key={country.code} 
                          value={country.code}
                          sx={{
                            fontFamily: TYPOGRAPHY.fontFamily.primary,
                            py: 1.5,
                            '&:hover': {
                              backgroundColor: THEME_COLORS.primary.main + '08'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '1.2rem' }}>
                              {country.flag}
                            </Typography>
                            <Typography sx={{ fontFamily: TYPOGRAPHY.fontFamily.primary }}>
                              {country.code}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Numéro de téléphone"
                    value={accompanistData.phoneNumber}
                    onChange={onAccompanistDataChange('phoneNumber')}
                    error={!!formErrors.phoneNumber}
                    helperText={formErrors.phoneNumber}
                    placeholder="123 456 789"
                    sx={fieldStyle}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        );
      } else if (participantType === 'futur_soldat') {
        return (
          <Box>
            {/* Header de section */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography 
                variant="h6"
                sx={{ 
                  fontFamily: TYPOGRAPHY.fontFamily.primary,
                  fontWeight: TYPOGRAPHY.fontWeight.semibold,
                  color: THEME_COLORS.text.primary,
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                🪖 Identification du futur soldat
              </Typography>
              <Typography 
                variant="body2"
                sx={{ 
                  color: THEME_COLORS.text.secondary,
                  fontFamily: TYPOGRAPHY.fontFamily.primary
                }}
              >
                Nous vérifierons si cette personne est déjà dans notre base de données
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <TextField
                fullWidth
                label="Adresse email du futur soldat"
                type="email"
                value={soldierData.email}
                onChange={onSoldierDataChange('email')}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
                placeholder="exemple@email.com"
                sx={fieldStyle}
              />

              <TextField
                fullWidth
                label="Confirmer l'adresse email"
                type="email"
                value={soldierData.confirmEmail}
                onChange={onSoldierDataChange('confirmEmail')}
                error={!!formErrors.confirmEmail}
                helperText={formErrors.confirmEmail}
                required
                placeholder="Retapez la même adresse email"
                sx={fieldStyle}
              />

              {/* Indicateur de correspondance des emails */}
              {soldierData.email && soldierData.confirmEmail && (
                <Box 
                  className="validation-indicator"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -2 }}>
                  {soldierData.email === soldierData.confirmEmail ? (
                    <>
                      <Typography sx={{ fontSize: '1.2rem' }}>✅</Typography>
                      <Typography 
                        variant="body2"
                        sx={{ 
                          color: THEME_COLORS.success.main,
                          fontFamily: TYPOGRAPHY.fontFamily.primary,
                          fontWeight: TYPOGRAPHY.fontWeight.medium
                        }}
                      >
                        Les adresses email correspondent
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ fontSize: '1.2rem' }}>❌</Typography>
                      <Typography 
                        variant="body2"
                        sx={{ 
                          color: THEME_COLORS.error.main,
                          fontFamily: TYPOGRAPHY.fontFamily.primary,
                          fontWeight: TYPOGRAPHY.fontWeight.medium
                        }}
                      >
                        Les adresses email ne correspondent pas
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        );
      }
    }

    // Étape 2: Informations supplémentaires pour futur soldat (si lead non trouvé)
    if (modalStep === 2 && participantType === 'futur_soldat' && !leadFound) {
      return (
        <Box>
          {/* Alert moderne */}
          <Alert 
            severity="info" 
            variant="filled"
            className="modern-alert"
            icon={<Typography sx={{ fontSize: '1.2rem' }}>🔍</Typography>}
            sx={{ 
              mb: 4,
              borderRadius: 3,
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              '& .MuiAlert-message': {
                fontWeight: TYPOGRAPHY.fontWeight.medium
              }
            }}
          >
            Aucun profil trouvé avec cet email. Saisissons les informations manuellement.
          </Alert>

          {/* Header de section */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography 
              variant="h6"
              sx={{ 
                fontFamily: TYPOGRAPHY.fontFamily.primary,
                fontWeight: TYPOGRAPHY.fontWeight.semibold,
                color: THEME_COLORS.text.primary,
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              📝 Informations personnelles
            </Typography>
            <Typography 
              variant="body2"
              sx={{ 
                color: THEME_COLORS.text.secondary,
                fontFamily: TYPOGRAPHY.fontFamily.primary
              }}
            >
              Complétez les informations du futur soldat
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* Ligne prénom/nom */}
            <Box sx={{ display: 'flex', gap: 3 }}>
              <TextField
                fullWidth
                label="Prénom"
                value={soldierData.firstName}
                onChange={onSoldierDataChange('firstName')}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
                required
                placeholder="Entrez le prénom"
                sx={fieldStyle}
              />

              <TextField
                fullWidth
                label="Nom"
                value={soldierData.lastName}
                onChange={onSoldierDataChange('lastName')}
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
                required
                placeholder="Entrez le nom de famille"
                sx={fieldStyle}
              />
            </Box>

            {/* Numéro de téléphone avec code pays moderne */}
            <Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 2, 
                  fontFamily: TYPOGRAPHY.fontFamily.primary,
                  fontWeight: TYPOGRAPHY.fontWeight.medium,
                  color: THEME_COLORS.text.primary
                }}
              >
                📞 Numéro de téléphone
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel
                    sx={{
                      fontFamily: TYPOGRAPHY.fontFamily.primary,
                      '&.Mui-focused': {
                        color: THEME_COLORS.secondary.main
                      }
                    }}
                  >
                    Pays
                  </InputLabel>
                  <Select
                    value={soldierData.countryCode}
                    onChange={onSoldierDataChange('countryCode')}
                    label="Pays"
                    sx={{
                      borderRadius: 3,
                      backgroundColor: THEME_COLORS.background.main,
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: THEME_COLORS.border.main,
                        borderWidth: '1.5px'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: THEME_COLORS.secondary.light,
                        borderWidth: '2px'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: THEME_COLORS.secondary.main,
                        borderWidth: '2px',
                        boxShadow: `0 0 0 3px ${THEME_COLORS.secondary.main}20`
                      }
                    }}
                  >
                    {countryCodes.map((country) => (
                      <MenuItem 
                        key={country.code} 
                        value={country.code}
                        sx={{
                          fontFamily: TYPOGRAPHY.fontFamily.primary,
                          py: 1.5,
                          '&:hover': {
                            backgroundColor: THEME_COLORS.secondary.main + '08'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: '1.2rem' }}>
                            {country.flag}
                          </Typography>
                          <Typography sx={{ fontFamily: TYPOGRAPHY.fontFamily.primary }}>
                            {country.code}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  label="Numéro de téléphone"
                  value={soldierData.phoneNumber}
                  onChange={onSoldierDataChange('phoneNumber')}
                  error={!!formErrors.phoneNumber}
                  helperText={formErrors.phoneNumber}
                  placeholder="123 456 789"
                  sx={fieldStyle}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      );
    }

    return null;
  };

  return (
    <Dialog 
      open={modalOpen} 
      onClose={onClose}
      maxWidth="md" 
      fullWidth
      className="modern-modal"
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 2, sm: 4 },
          minHeight: { xs: 'auto', sm: 400 },
          m: { xs: 1, sm: 2 },
          background: THEME_COLORS.background.glass,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${THEME_COLORS.border.light}`,
          boxShadow: THEME_COLORS.shadow.lg,
          overflow: 'hidden',
          position: 'relative',
          maxHeight: { xs: '95vh', sm: '90vh' },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: { xs: '60px', sm: '80px' },
            background: THEME_COLORS.primary.gradient,
            opacity: 0.08,
            zIndex: 0
          }
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(6px)'
        }
      }}
    >
      {/* Header plus fin et responsive */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          p: { xs: 3, sm: 4 },
          pb: { xs: 2, sm: 2 },
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}
      >
        <Box>
          <Typography 
            variant="h4"
            sx={{ 
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              fontWeight: TYPOGRAPHY.fontWeight.bold,
              fontSize: { xs: TYPOGRAPHY.fontSize.xl, sm: TYPOGRAPHY.fontSize.xl },
              background: THEME_COLORS.primary.gradient,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: { xs: 0.5, sm: 1 }
            }}
          >
            👥 Ajouter un participant
          </Typography>
          <Typography 
            variant="body1"
            sx={{ 
              color: THEME_COLORS.text.secondary,
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.base }
            }}
          >
            Complétez les informations en {getSteps().length} étapes
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            backgroundColor: THEME_COLORS.background.paper,
            boxShadow: THEME_COLORS.shadow.sm,
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            alignSelf: { xs: 'flex-end', sm: 'auto' },
            '&:hover': {
              backgroundColor: THEME_COLORS.error.main,
              color: 'white',
              transform: { xs: 'scale(1.05)', sm: 'scale(1.1)' }
            },
            transition: ANIMATIONS.transition.normal
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ 
        px: { xs: 2, sm: 4 }, 
        py: { xs: 1, sm: 2 }, 
        position: 'relative', 
        zIndex: 2,
        maxHeight: { xs: '70vh', sm: 'none' },
        overflowY: 'auto'
      }}>
        {/* Stepper plus fin */}
        <Card
          sx={{
            mb: { xs: 3, sm: 4 },
            background: THEME_COLORS.background.paper,
            borderRadius: { xs: 2, sm: 3 },
            border: `1px solid ${THEME_COLORS.border.light}`,
            boxShadow: THEME_COLORS.shadow.sm
          }}
        >
          <CardContent sx={{ py: { xs: 2, sm: 3 } }}>
            <Stepper 
              activeStep={modalStep}
              sx={{
                '& .MuiStepConnector-line': {
                  borderColor: THEME_COLORS.border.main
                }
              }}
            >
              {getSteps().map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontFamily: TYPOGRAPHY.fontFamily.primary,
                        fontWeight: TYPOGRAPHY.fontWeight.medium,
                        fontSize: TYPOGRAPHY.fontSize.sm
                      },
                      '& .MuiStepIcon-root': {
                        fontSize: '1.5rem',
                        '&.Mui-active': {
                          color: participantType === 'accompagnateur' 
                            ? THEME_COLORS.primary.main 
                            : THEME_COLORS.secondary.main
                        },
                        '&.Mui-completed': {
                          color: THEME_COLORS.success.main
                        }
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        {/* Contenu de l'étape avec animation */}
        <Fade in={true} timeout={300}>
          <Card
            sx={{
              background: THEME_COLORS.background.paper,
              borderRadius: 4,
              border: `1px solid ${THEME_COLORS.border.light}`,
              boxShadow: THEME_COLORS.shadow.md,
              overflow: 'hidden'
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
              {renderStepContent()}
            </CardContent>
          </Card>
        </Fade>

        {/* Boutons d'action responsive */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mt: { xs: 3, sm: 4 }, 
          mb: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Button 
            onClick={onClose}
            variant="outlined"
            size="large"
            sx={{
              px: { xs: 3, sm: 4 },
              py: { xs: 1.25, sm: 1.5 },
              borderRadius: { xs: 2, sm: 3 },
              borderColor: THEME_COLORS.border.main,
              color: THEME_COLORS.text.secondary,
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              fontWeight: TYPOGRAPHY.fontWeight.medium,
              fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.sm },
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              '&:hover': {
                borderColor: THEME_COLORS.error.main,
                color: THEME_COLORS.error.main,
                backgroundColor: THEME_COLORS.error.main + '08'
              }
            }}
          >
            ❌ Annuler
          </Button>
          
          <Button
            variant="contained"
            size="large"
            onClick={handleNext}
            disabled={!isNextButtonEnabled() || searchingLead}
            sx={{
              px: { xs: 4, sm: 6 },
              py: { xs: 1.25, sm: 1.5 },
              borderRadius: { xs: 2, sm: 3 },
              background: participantType === 'accompagnateur' 
                ? THEME_COLORS.primary.gradient 
                : THEME_COLORS.secondary.gradient,
              color: 'white',
              fontFamily: TYPOGRAPHY.fontFamily.primary,
              fontWeight: TYPOGRAPHY.fontWeight.semibold,
              fontSize: { xs: TYPOGRAPHY.fontSize.sm, sm: TYPOGRAPHY.fontSize.sm },
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
              boxShadow: participantType === 'accompagnateur' 
                ? `0 4px 16px ${THEME_COLORS.primary.main}30`
                : `0 4px 16px ${THEME_COLORS.secondary.main}30`,
              transition: ANIMATIONS.transition.normal,
              '&:hover:not(:disabled)': {
                transform: { xs: 'none', sm: 'translateY(-1px)' },
                boxShadow: participantType === 'accompagnateur' 
                  ? `0 6px 20px ${THEME_COLORS.primary.main}40`
                  : `0 6px 20px ${THEME_COLORS.secondary.main}40`
              },
              '&:active:not(:disabled)': {
                transform: 'translateY(0px)'
              },
              '&:disabled': {
                background: THEME_COLORS.text.muted,
                boxShadow: 'none',
                cursor: 'not-allowed'
              }
            }}
            startIcon={searchingLead ? 
              <CircularProgress size={16} sx={{ color: 'white' }} /> : 
              <Typography sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                {modalStep === 0 ? '➡️' : '✅'}
              </Typography>
            }
          >
            {searchingLead 
              ? 'Recherche...' 
              : (modalStep === 0 ? 'Suivant' : 'Terminer')
            }
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ParticipantModal;